import { Box, Card, Checkbox, Collapse, IconButton, Stack, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { GridLayout, HeadCellsCollection } from 'shared/ui/grid-layout';
import { NotificationsConfig, useSnack } from 'shared/ui/snack';
import { BA_TYPES, BankAccountDto, InvoiceDto, IPagingData, PayOrderDto, PayOrderGroup, PayOrderRowDto, PayOrderStatus, PO_GROUPS, POS_STATUSES, UpdatePayOrderStatusDto } from '@sr/dto';
import { formatDate } from 'utils/date-format-helpers';
import PriceSpan from 'shared/ui/price-span';
import { LinkToInvoice } from 'entities/client-invoices-list/ui/link-to-invoice';
import { LinkToLegalEntity } from 'shared/ui/links/link-to-legal-entity';
import { IRemotePagedCollection } from 'utils/remote-paged-collection.hook';
import { PayordersFilter } from 'features/payorders/payorders-filter';
import { BaseSyntheticEvent, MouseEvent, useState } from 'react';
import { InvoiceStatusSpan } from '../../entities/invoice/status-span/status-span';
import { LinkToPayOrder } from 'shared/ui/links/link-to-payorder';
import { POG_EMPLOYEE_PAYMENT, POS_CONFIRMED, POS_DRAFT, POS_PAID } from '@sr/dto/dist/src/enums/payorder';
import { payOrdersApi } from 'entities/pay-orders/pay-orders.api';
import { extractNestJsErrorMessage } from 'shared/api/rtk-query';

const columns = ['Номер ПП', 'Дата', 'Тип', 'Группа', 'Статус', 'Сумма', 'Счет', 'Назначение платежа'];

const invoiceColumns = ['Номер счета', 'Дата выставления счета', 'Юрлицо плательщика', 'Юрлицо получателя', 'Статус счета'];

export const notificationsConfig: NotificationsConfig = {
  itemTitleForm1: 'платежное поручение',
  itemTitleForm2: 'платежного поручения',
  gender: 'neutral'
};

type FilterableList = { filterString: string; onFilter: (filterString: string) => void } | { filterString?: undefined; onFilter?: undefined };

type RowSelectProps = ({ onRowSelect: SelectHandler; selectedRows: number[] } | { onRowSelect?: undefined; selectedRows?: undefined });

type PagingProps = {
  collection: IRemotePagedCollection<PayOrderDto>;
  paging: IPagingData;
  onChangePaging?: (paging: IPagingData) => void;
}

type Props =
  PagingProps &
  RowSelectProps &
  EditableRowProps &
  FilterableList;

type SelectHandler = (rowId: number, value: boolean) => void;

export const PayOrdersList = ({ collection, paging, onRowSelect, selectedRows, onChangePaging, onEditClick, onDeleteClick }: Props) => {
  const gridColumns: HeadCellsCollection = onRowSelect
    ? [
      {
        label: (
          <SelectOneOrAllCell
            onRowSelect={onRowSelect}
            isSelected={!!collection.collection.items.length && collection.collection.items.length === selectedRows?.length}
            ids={collection.collection.items.map((item) => item.id)}
          />
        )
      },
      ...columns
    ]
    : columns;

  if (onEditClick && !gridColumns.includes('Действия')) {
    gridColumns.push('Действия');
  }

  const metaAmountTotal = collection.collection.meta?.find((x) => x['amountTotal'])?.['amountTotal'];
  

  return (
    <>
      <Box my={1}>
        <PayordersFilter />
      </Box>
      <GridLayout
        columns={gridColumns}
        items={collection.collection.items}
        isLoading={collection.isLoading}
        error={collection.error}
        pagingData={paging}
        onChangePaging={onChangePaging}
        totalItemsCount={collection.collection.totalItemsCount}
        itemRender={(payOrder) => {
          let selectableProps: SelectableRowProps = { onRowSelect: undefined, isSelected: undefined };
          if (onRowSelect && selectedRows) {
            selectableProps = { onRowSelect: onRowSelect, isSelected: !!selectedRows.find((id) => id === payOrder.id) };
          }
          let editableProps: EditableRowProps = { onDeleteClick: undefined, onEditClick: undefined };
          if (onEditClick && onDeleteClick) {
            editableProps = { onEditClick: onEditClick, onDeleteClick: onDeleteClick };
          }
          return <Row {...selectableProps} {...editableProps} key={payOrder.id} payOrder={payOrder} colSpan={gridColumns.length} />;
        }}
        noItemsText="Список платежных поручений пуст"
        renderCustomHeader={() => <PayorderListHeader rows={collection.collection.items} metaAmountTotal={metaAmountTotal} />}
        footer={(items) => <PayOrderRowFooter rows={items} metaAmountTotal={metaAmountTotal}/>}
      />
    </>
  );
};

type RowProps = {
  payOrder: PayOrderDto;
  onRowSelect?: SelectHandler;
  colSpan: number;
} & SelectableRowProps &
  EditableRowProps;

type SelectableRowProps = { onRowSelect: SelectHandler; isSelected: boolean } | { onRowSelect: undefined; isSelected: undefined };

type EditableRowProps =
  | { onEditClick: (payOrder: PayOrderDto, dto?: any) => void; onDeleteClick: (payOrder: PayOrderDto) => void }
  | { onEditClick?: undefined; onDeleteClick?: undefined };

const Row = ({ payOrder, onEditClick, onDeleteClick, onRowSelect, isSelected, colSpan }: RowProps) => {
  const [open, setOpen] = useState(false);
  const [updateStatusItem] = payOrdersApi.useUpdatePayOrderStatusMutation();
  const { showSuccess, showError } = useSnack();

  const handleRowClick = () => {
    setOpen((prev) => !prev);
  };

  const handleRowDelete = (e: MouseEvent) => {
    e.stopPropagation();
    if (onDeleteClick) {
      onDeleteClick(payOrder);
    }
  };

  const handleRowEdit = (e: MouseEvent) => {
    e.stopPropagation();
    if (onEditClick) {
      onEditClick(payOrder);
    }
  };

  const handleEditStatusPayOrder = async () => {
    const updatePayOrderStatusDto: UpdatePayOrderStatusDto = {
      id: payOrder.id,
      paymentDate: payOrder.paymentDate,
      status: POS_PAID
    };
  
    try {
      await updateStatusItem(updatePayOrderStatusDto).unwrap();
      showSuccess('Запись успешно обновлена');
    } catch (error) {
      showError('Ошибка обновления записи');
      throw extractNestJsErrorMessage(error);
    }
  };

  return (
    <>
      <TableRow sx={{ cursor: 'pointer' }} onClick={handleRowClick}>
        {onRowSelect && (
          <TableCell>
            <SelectOneOrAllCell ids={[payOrder.id]} isSelected={isSelected} onRowSelect={onRowSelect} />
          </TableCell>
        )}
        <TableCell>
          <LinkToPayOrder payorder={payOrder}>{payOrder.documentNumber}</LinkToPayOrder>
        </TableCell>
        <TableCell>{formatDate(payOrder.paymentDate)}</TableCell>
        <TableCell>{BA_TYPES[payOrder.bankAccount.type]}</TableCell>
        <TableCell>{PO_GROUPS[payOrder.group as PayOrderGroup]}</TableCell>
        <TableCell>{POS_STATUSES[payOrder.status as PayOrderStatus]}</TableCell>
        <TableCell>
          <PriceSpan price={payOrder.rows.reduce((acc, row) => acc + row.amount, 0)} size="small" />
        </TableCell>
        <TableCell>
          {
            payOrder.rows.map(x => (
              x.invoice ? <LinkToInvoice invoice={x.invoice} /> : <>-</>
            ))
          }
        </TableCell>
        <TableCell>
          <InvoicePaymentDescription payorder={payOrder} />
        </TableCell>
        {onEditClick && (
          <TableCell align="right" width={150}>
            { 
              (payOrder.group === POG_EMPLOYEE_PAYMENT && (payOrder.status === POS_DRAFT ||  payOrder.status === POS_CONFIRMED)) && (
                <Tooltip title="Выплатить">
                  <IconButton edge="end" onClick={handleEditStatusPayOrder} >
                    <DoneAllIcon color='success'/>
                  </IconButton>
                </Tooltip>
              )
            }
            <IconButton edge="end" onClick={handleRowEdit}>
              <EditIcon />
            </IconButton>
            <IconButton edge="end" onClick={handleRowDelete}>
              <DeleteIcon />
            </IconButton>
          </TableCell>
        )}
      </TableRow>

      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={colSpan}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box marginY={1}>
              {payOrder.counterpartyAccount
                ? <TransferCounterpartyRow counterpartyAccount={payOrder.counterpartyAccount} />
                : <GridLayout<PayOrderRowDto>
                  columns={invoiceColumns}
                  items={payOrder.rows}
                  isLoading={false}
                  itemRender={(payOrderRow) => payOrderRow.invoiceId && <InvoiceSubRow key={payOrderRow.invoiceId} invoice={payOrderRow.invoice} />}
                  noItemsText="Нет привязанных счетов"
                />}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default PayOrdersList;

const InvoiceSubRow = ({ invoice }: { invoice: InvoiceDto }) => {
  return (
    <TableRow>
      <TableCell>
        <LinkToInvoice invoice={invoice} />
      </TableCell>
      <TableCell>
        <LinkToInvoice invoice={invoice}>{formatDate(invoice.startDate)}</LinkToInvoice>
      </TableCell>
      <TableCell>
        <LinkToLegalEntity legalEntity={invoice.seller} />
      </TableCell>
      <TableCell>
        <LinkToLegalEntity legalEntity={invoice.buyer} />
      </TableCell>
      <TableCell>
        <InvoiceStatusSpan status={invoice.status} size="small" />
      </TableCell>
    </TableRow>
  );
};

const TransferCounterpartyRow = ({ counterpartyAccount }: { counterpartyAccount: BankAccountDto }) => {
  return (
    <Typography variant="body2" color="grey.700" p={1}>
      Переброска между своими счетами. Второй счёт: {counterpartyAccount.title}
    </Typography>
  );
};

const InvoicePaymentDescription = ({ payorder }: { payorder: PayOrderDto }) => {
  return (
    <Typography component="span" variant="inherit" fontStyle="italic">
      {payorder.description}
    </Typography>
  );
};

const BankAccountSpan = ({ bankAccount }: { bankAccount: BankAccountDto }) => (
  <Typography variant="inherit" color={bankAccount.isActive ? 'inherit' : 'grey.500'}>
    {bankAccount.title}
  </Typography>
);

const SelectOneOrAllCell = ({ onRowSelect, isSelected, ids }: { onRowSelect: SelectHandler; isSelected: boolean; ids: number[] }) => {
  const handleCheckChange = (_: BaseSyntheticEvent, value: boolean) => {
    ids.forEach((id) => {
      onRowSelect(id, value);
    });
  };
  return <Checkbox onChange={handleCheckChange} checked={isSelected} />;
};

type PayOrderRowFooterProps = {
  rows: PayOrderDto[];
  metaAmountTotal?: number;
};
const PayOrderRowFooter = ({ rows, metaAmountTotal }: PayOrderRowFooterProps) => {
  const totalAmount = metaAmountTotal ?? rows.reduce((total, payOrder) => total + payOrder.rows.reduce((sum, row) => sum + row.amount, 0), 0);
  return (
    <TableRow>
      <TableCell colSpan={5} align="right">
        <Typography variant="subtitle1">Итого:</Typography>
      </TableCell>
      <TableCell align="left">
        <PriceSpan price={totalAmount} size="small" />
      </TableCell>
      <TableCell />
      <TableCell />
      <TableCell />
    </TableRow>
  );
};

type PayorderListHeaderProps = {
  rows: PayOrderDto[];
  metaAmountTotal: number | undefined;
};
const PayorderListHeader = ({ rows, metaAmountTotal }: PayorderListHeaderProps) => {
  const totalAmount = metaAmountTotal ? metaAmountTotal : rows.reduce((total, payOrder) => total + payOrder.rows.reduce((sum, row) => sum + row.amount, 0), 0);

  return (
    <Card sx={{ mb: 2, padding: 1, paddingLeft: 2, paddingRight: 2 }}>
      <Stack direction={'row'} gap={8}>
        <Stack direction={'column'}>
          <Typography>Итого:</Typography>
        </Stack>

        <Stack direction={'row'} gap={4}>
          <Stack direction={'column'}>
            <Typography>Сумма</Typography>
            <Typography>
              <PriceSpan price={totalAmount} size="small" />
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );
};
